import { useEffect, useState } from "react";

import { workbenchDocumentsApi } from "../../../api/client";

interface DocumentContent {
  src: string;
  contentType: string;
}

interface UseDocumentContentState {
  content: DocumentContent | undefined;
  isLoading: boolean;
  error: Error | undefined;
}

/**
 *
 * @param appId
 * @param documentId
 * @param enabled
 */
export function useDocumentContent(appId: string, documentId: string, enabled: boolean): UseDocumentContentState {
  const [state, setState] = useState<UseDocumentContentState>({
    content: undefined,
    isLoading: enabled,
    error: undefined,
  });

  useEffect(() => {
    if (!enabled || !appId || !documentId) {
      setState({ content: undefined, isLoading: false, error: undefined });
      return;
    }

    setState({ content: undefined, isLoading: true, error: undefined });

    let isMounted = true;
    let blobUrl: string | undefined;
    const abortController = new AbortController();

    const fetchContent = async () => {
      try {
        const response = await workbenchDocumentsApi.getDocumentContent(appId, documentId, {
          responseType: "arraybuffer",
          signal: abortController.signal,
        });

        if (!isMounted) {
          return;
        }

        const data = response.data;
        if (!data) {
          throw new Error("No data in response");
        }

        const contentType = (response.headers?.["content-type"] as string) || "application/octet-stream";
        const blob = new Blob([data], { type: contentType });
        blobUrl = URL.createObjectURL(blob);

        setState({
          content: {
            src: blobUrl,
            contentType,
          },
          isLoading: false,
          error: undefined,
        });
      } catch (error_) {
        if (isMounted && !(error_ instanceof Error && error_.name === "AbortError")) {
          const error = error_ instanceof Error ? error_ : new Error(String(error_));
          // eslint-disable-next-line no-console
          console.error("useDocumentContent error:", error);
          setState({
            content: undefined,
            isLoading: false,
            error,
          });
        }
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
      abortController.abort();
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [appId, documentId, enabled]);

  return state;
}
